import { useMemo } from 'react'
import { RoleType } from '../../models/Role'
import { type SeatPurchase, useGetInvitesQuery } from '../../store/auth/auth'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { useGetBillingCustomerFreeSeatsQuery } from '../../store/plan/plan'
import { freeSeatsFor } from './requestedGroups'
import { requestedSeatTypeCountFilter } from './teamQueries'
import { useAssistantSeatsOffered } from './useAssistantSeatsOffered'

const SNAPSHOT_PAGE = { page: 0, size: 500 }

export interface RequestedSeatsSnapshot {
  invitationIds: string[]
  total: number
  purchase: SeatPurchase | null
  isLoading: boolean
}

export const seatShortage = (requested: number, freeSeats?: number): number | null => (freeSeats === undefined ? null : Math.max(0, requested - freeSeats))

export const useRequestedSeatsSnapshot = (): RequestedSeatsSnapshot => {
  const accountId = useAppSelector(selectAccountId)
  const advisorFilter = useMemo(() => requestedSeatTypeCountFilter(RoleType.SEAT), [])
  const assistantFilter = useMemo(() => requestedSeatTypeCountFilter(RoleType.SIDECAR), [])

  const { offered: assistantOffered, isLoading: offerLoading } = useAssistantSeatsOffered()
  const { data: freeSeatsData, isFetching: freeSeatsFetching } = useGetBillingCustomerFreeSeatsQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const advisor = useGetInvitesQuery({ accountId, pageRequest: SNAPSHOT_PAGE, filter: advisorFilter }, { refetchOnMountOrArgChange: true })
  const assistant = useGetInvitesQuery(
    { accountId, pageRequest: SNAPSHOT_PAGE, filter: assistantFilter },
    { refetchOnMountOrArgChange: true, skip: !assistantOffered },
  )

  return useMemo(() => {
    const advisorRows = advisor.data?.data ?? []
    const assistantRows = assistantOffered ? (assistant.data?.data ?? []) : []
    const seats = freeSeatsData?.data.seats
    const general = seatShortage(advisorRows.length, freeSeatsFor(RoleType.SEAT, seats))
    const sidecar = assistantOffered ? seatShortage(assistantRows.length, freeSeatsFor(RoleType.SIDECAR, seats)) : 0

    return {
      invitationIds: [...advisorRows, ...assistantRows].map((invite) => invite.id),
      total: advisorRows.length + assistantRows.length,
      purchase: general === null || sidecar === null ? null : { general, sidecar },
      isLoading: advisor.isFetching || assistant.isFetching || freeSeatsFetching || offerLoading,
    }
  }, [advisor.data, advisor.isFetching, assistant.data, assistant.isFetching, assistantOffered, freeSeatsData, freeSeatsFetching, offerLoading])
}
